import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { deleteCookie } from 'cookies-next';
import type { IncomingMessage, ServerResponse } from 'http';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';

import { graphql } from '@/graphql/__generated/gql';
import { appLogger } from '@/utils/app-logger/appLogger';
import { prefetchRequest, useRequest } from '@/utils/request';
import { getLOB } from '@/utils/server/lob-utils';
import { getRegion } from '@/utils/server/region-utils';

import {
    getLocation,
    getLocationFromCookie,
    getLocationFromLatLon,
    prepareLocationFromPosition,
    saveLocationToCookie,
    successfulLocationFromBrowser,
} from '../data-sources-apis/location-api';
import type { Coordinates, PositionDetails } from '../utils/configs/location';

const ONE_MINUTE = 60000;
const COOKIE_NAME = 'latlon';
const MAX_AGE = ONE_MINUTE * 60;
const LOCATION_COOKIE_MAX_AGE = 24 * 60 * 60;

export const TN_LOCATION_STALE_TIME_MS = 60 * 1000;
// Browser only: on the server a finite gcTime arms a timer that outlives the
// request and pins the per-request QueryClient in memory until it fires.
export const TN_LOCATION_GC_TIME_MS = 5 * 60 * 1000;

const LATLON_DISABLED = new Set(['false', 'off', '-1']);
const LOCATION_QUERY_CACHE_KEY = 'location-resolver';

const locationResolverDocument = /* GraphQL */ `
    query LocationResolver($fallback: Boolean) {
        locationResolver(fallback: $fallback) {
            coords {
                latitude
                longitude
            }
            isFallback
            source
        }
    }
`;
type LocationResolverQueryResult = {
    locationResolver: PositionDetails | null;
};

type LocationResolverQueryVariables = {
    fallback?: boolean;
};

const locationResolverQuery = graphql(locationResolverDocument) as TypedDocumentNode<
    LocationResolverQueryResult,
    LocationResolverQueryVariables
>;

/** React Query key shared by `prefetchLocation` and `useLocation` for SSR hydration. */
export const TN_LOCATION_QUERY_KEY = ['tn', 'user-location'] as const;

export type ResolveLocationSSRParams = {
    req: IncomingMessage;
    query: ParsedUrlQuery;
    res?: ServerResponse;
};

/** Returns the first string value for a query param key, handling array values from the Pages router. */
function getFirstQueryValue(query: ParsedUrlQuery, key: string): string | undefined {
    const value = query[key];
    if (Array.isArray(value)) {
        return value[0];
    }
    return value;
}

/** Returns true if the `latlon` value explicitly opts out of location spoofing (e.g. "false", "off", "-1"). */
function isLatLonQueryDisabled(latlon: string): boolean {
    return LATLON_DISABLED.has(latlon.trim().toLowerCase());
}

/**
 * Parses search params from an IncomingMessage URL (Pages router / Node request).
 */
export function getQueryFromIncomingMessage(req: IncomingMessage): ParsedUrlQuery {
    if (!req.url) {
        return {};
    }
    try {
        const rawHost = req.headers.host;
        const host = Array.isArray(rawHost)
            ? (rawHost[0] ?? 'localhost')
            : (rawHost ?? 'localhost');
        const fullUrl = req.url.startsWith('http') ? req.url : `http://${host}${req.url}`;
        const url = new URL(fullUrl);
        const result: ParsedUrlQuery = {};
        url.searchParams.forEach((value, key) => {
            const existing = result[key];
            if (existing === undefined) {
                result[key] = value;
            } else if (Array.isArray(existing)) {
                existing.push(value);
            } else {
                result[key] = [existing, value];
            }
        });
        return result;
    } catch (err) {
        appLogger.debug('getQueryFromIncomingMessage: failed to parse URL', {
            url: req.url,
            error: err instanceof Error ? err.message : String(err),
        });
        return {};
    }
}

/** Converts an `IncomingMessage` headers object into a Fetch-compatible `Headers` instance. */
function buildHeadersFromIncomingMessage(req: IncomingMessage): Headers {
    const headers = new Headers();
    const raw = req.headers;
    for (const [key, value] of Object.entries(raw)) {
        if (value === undefined) {
            continue;
        }
        headers.set(key.toLowerCase(), Array.isArray(value) ? value.join(', ') : value);
    }
    return headers;
}

/**
 * Server-side resolution: uses `location-rest-api` only (no HTTP round-trip).
 * Does not set cookies; clears `latlon` only when the query param explicitly disables spoofing
 * and writable `res` is available.
 */
export async function resolveLocationForSSR({
    req,
    query,
    res,
}: ResolveLocationSSRParams): Promise<PositionDetails> {
    const latlonRaw = getFirstQueryValue(query, 'latlon');

    if (latlonRaw !== undefined && latlonRaw !== null && latlonRaw !== '') {
        if (isLatLonQueryDisabled(latlonRaw)) {
            if (res) {
                deleteCookie(COOKIE_NAME, { req, res, path: '/' });
            }
        } else {
            const fromQuery = getLocationFromLatLon(latlonRaw);
            if (fromQuery) {
                return fromQuery;
            }
        }
    }

    const fromCookie = getLocationFromCookie(COOKIE_NAME, false, { req, res });
    if (fromCookie) {
        return fromCookie;
    }

    const headers = buildHeadersFromIncomingMessage(req);
    return getLocation(headers, false, getRegion(req), getLOB(req));
}

export type PrefetchLocationOptions = {
    /** Optional response used to emit `Set-Cookie` when SSR clears `latlon` after spoofing is disabled. */
    res?: ServerResponse;
    /**
     * Query from `getServerSideProps` (preferred). If it is missing, it is parsed from `req.url`
     * (e.g. `_next/data` requests may carry the query only in the body/internal URL).
     */
    query?: ParsedUrlQuery;
};

/**
 * Prefetches location into React Query for SSR hydration (`ssrState` / `prefetchSSR` / `fetchSSR`).
 */
export const prefetchLocation =
    (options?: PrefetchLocationOptions) =>
    (
        req: IncomingMessage,
    ): {
        queryKey: [...typeof TN_LOCATION_QUERY_KEY];
        queryFn: () => Promise<PositionDetails>;
    } => ({
        queryKey: [...TN_LOCATION_QUERY_KEY],
        queryFn: async () => {
            const query = options?.query ?? getQueryFromIncomingMessage(req);
            const latlonRaw = getFirstQueryValue(query, 'latlon');

            if (latlonRaw !== undefined && latlonRaw !== null && latlonRaw !== '') {
                if (isLatLonQueryDisabled(latlonRaw)) {
                    if (options?.res) {
                        deleteCookie(COOKIE_NAME, { req, res: options.res, path: '/' });
                    }
                } else {
                    const fromQuery = getLocationFromLatLon(latlonRaw);
                    if (fromQuery) {
                        return fromQuery;
                    }
                }
            }

            const fromCookie = getLocationFromCookie(COOKIE_NAME, false, {
                req,
                res: options?.res,
            });
            if (fromCookie) {
                return fromCookie;
            }

            const prefetchedLocation = prefetchRequest<
                LocationResolverQueryResult,
                LocationResolverQueryVariables
            >({
                cacheKey: LOCATION_QUERY_CACHE_KEY,
                query: locationResolverQuery,
                dataParams: { fallback: false },
                req,
            });

            const data = await prefetchedLocation.queryFn();
            const fromGraphql = data?.locationResolver;
            if (fromGraphql) {
                return fromGraphql;
            }

            const headers = buildHeadersFromIncomingMessage(req);
            return getLocation(headers, false, getRegion(req), getLOB(req));
        },
    });

/** Arguments for client-side location resolution. */
type FetchLocationClientArgs = {
    fallback: boolean;
    fetchLocationFromGraphQL: () => Promise<PositionDetails | null>;
};

/**
 * Fetches the user's location from the server-side geolocation API route.
 * Saves the result to the latlon cookie on success. Returns `null` on any failure.
 */
async function fetchLocationOnClient({
    fallback,
    fetchLocationFromGraphQL,
}: FetchLocationClientArgs): Promise<PositionDetails> {
    const urlParams = new URLSearchParams(window.location.search);
    const latlon = urlParams.get('latlon');

    if (latlon !== null && latlon !== '') {
        if (isLatLonQueryDisabled(latlon)) {
            deleteCookie(COOKIE_NAME);
        } else {
            const fromLatLon = getLocationFromLatLon(latlon);
            if (fromLatLon) {
                saveLocationToCookie(COOKIE_NAME, fromLatLon, LOCATION_COOKIE_MAX_AGE);
                return fromLatLon;
            }
        }
    }

    const fromCookie = getLocationFromCookie(COOKIE_NAME, fallback);
    if (fromCookie) {
        return fromCookie;
    }

    const geoOptions = {
        timeout: 10000,
        maximumAge: MAX_AGE,
        enableHighAccuracy: false,
    };

    if (
        !fallback &&
        typeof navigator !== 'undefined' &&
        'geolocation' in navigator &&
        navigator.geolocation
    ) {
        const fromBrowser = await new Promise<PositionDetails | null>((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    const loc = successfulLocationFromBrowser(position, fallback);
                    if (loc) {
                        saveLocationToCookie(COOKIE_NAME, loc, LOCATION_COOKIE_MAX_AGE);
                        resolve(loc);
                    } else {
                        resolve(null);
                    }
                },
                () => {
                    resolve(null);
                },
                geoOptions,
            );
        });

        if (fromBrowser) {
            return fromBrowser;
        }
    }

    const fromGraphQL = await fetchLocationFromGraphQL();
    if (fromGraphQL) {
        return fromGraphQL;
    }

    return prepareLocationFromPosition({ latitude: 0, longitude: 0 }, true, 'default');
}

const WATCH_GEOLOCATION_THRESHOLD = 0.1; // ~11 km — significant enough to change DMA market

/**
 * When geolocation permission is already granted (no prompt), watches for position changes
 * that exceed the threshold and busts the location cookie + React Query cache.
 * Covers devtools sensor changes and real-device movement without prompting users.
 */
function useWatchBrowserGeolocation(
    queryClient: ReturnType<typeof useQueryClient>,
    isBrowser: boolean,
): void {
    const lastWatchCoordsRef = useRef<Coordinates | null>(null);

    useEffect(() => {
        if (
            !isBrowser ||
            typeof navigator === 'undefined' ||
            !('geolocation' in navigator) ||
            !navigator.permissions
        ) {
            return;
        }

        let watchId: number | null = null;

        navigator.permissions
            .query({ name: 'geolocation' as PermissionName })
            .then((result) => {
                if (result.state !== 'granted') return;

                watchId = navigator.geolocation.watchPosition(
                    (position) => {
                        const newCoords: Coordinates = {
                            latitude: position.coords.latitude,
                            longitude: position.coords.longitude,
                        };
                        const prev = lastWatchCoordsRef.current;
                        const hasChangedSignificantly =
                            !prev ||
                            Math.abs(prev.latitude - newCoords.latitude) >
                                WATCH_GEOLOCATION_THRESHOLD ||
                            Math.abs(prev.longitude - newCoords.longitude) >
                                WATCH_GEOLOCATION_THRESHOLD;

                        if (hasChangedSignificantly) {
                            lastWatchCoordsRef.current = newCoords;
                            saveLocationToCookie(
                                COOKIE_NAME,
                                prepareLocationFromPosition(newCoords, false, 'browser'),
                                LOCATION_COOKIE_MAX_AGE,
                            );
                            queryClient.invalidateQueries({
                                queryKey: [...TN_LOCATION_QUERY_KEY],
                            });
                        }
                    },
                    (err) => {
                        appLogger.debug('geolocation watchPosition error', {
                            code: err.code,
                            message: err.message,
                        });
                    },
                    { timeout: 10000, maximumAge: 0, enableHighAccuracy: false },
                );
            })
            .catch((err) => {
                appLogger.debug('navigator.permissions.query failed for geolocation', {
                    error: err instanceof Error ? err.message : String(err),
                });
            });

        return () => {
            if (watchId !== null) {
                navigator.geolocation.clearWatch(watchId);
            }
        };
    }, [isBrowser, queryClient]);
}

/**
 * Formats a React Query error into a user-facing string.
 * Returns `null` when there is no error or no error value.
 */
export function formatLocationQueryError(isError: boolean, queryError: unknown): string | null {
    if (!isError || !queryError) {
        return null;
    }
    return queryError instanceof Error ? queryError.message : 'Failed to fetch location.';
}

/**
 * Location hook aligned with `prefetchLocation` / `resolveLocationForSSR`.
 * Uses React Query: the same `queryKey` as `prefetchLocation` allows hydrating the SSR cache
 * via `HydrationBoundary` in `_app.tsx`.
 */
export default function useLocation(
    fallback = false,
    enabled = true,
): {
    location: PositionDetails | null;
    error: string | null;
    refetch: () => Promise<PositionDetails | null>;
} {
    const isBrowser = typeof window !== 'undefined';
    const queryClient = useQueryClient();
    const router = useRouter();
    const fallbackRefetchAttempted = useRef(false);
    const lastSearchRef = useRef(isBrowser ? window.location.search : '');
    const { refetch: refetchLocationResolver } = useRequest<
        LocationResolverQueryResult,
        LocationResolverQueryVariables
    >({
        cacheKey: LOCATION_QUERY_CACHE_KEY,
        query: locationResolverQuery,
        dataParams: { fallback },
        requestOptions: {
            enabled: false,
            staleTime: TN_LOCATION_STALE_TIME_MS,
            gcTime: isBrowser ? TN_LOCATION_GC_TIME_MS : undefined,
            retry: 1,
        },
    });

    // The listeners below invalidate the SHARED location query and can delete the
    // location cookie — a disabled useLocation must not register them, or an
    // inactive consumer (e.g. DMAProvider while signed out) mutates state that
    // belongs to active ones.
    useEffect(() => {
        if (!isBrowser || !enabled) return;

        const syncToSearch = (nextSearch: string) => {
            const prevSearch = lastSearchRef.current;

            if (nextSearch !== prevSearch) {
                queryClient.invalidateQueries({ queryKey: [...TN_LOCATION_QUERY_KEY] });
            }

            if (prevSearch.includes('latlon=') && !nextSearch.includes('latlon=')) {
                deleteCookie(COOKIE_NAME);
            }

            lastSearchRef.current = nextSearch;
        };

        // While disabled these listeners are not registered, so lastSearchRef can go
        // stale across navigations. Catch up on (re-)enable, or the first route change
        // afterwards compares against an outdated search string and can skip the
        // latlon cookie cleanup or a needed invalidation.
        syncToSearch(window.location.search);

        const handleRouteChange = (url: string) => {
            syncToSearch(url.includes('?') ? url.slice(url.indexOf('?')) : '');
        };

        router.events.on('routeChangeComplete', handleRouteChange);
        return () => {
            router.events.off('routeChangeComplete', handleRouteChange);
        };
    }, [isBrowser, router.events, queryClient, enabled]);

    useEffect(() => {
        if (!isBrowser || !enabled) return;

        const handlePopState = () => {
            const nextSearch = window.location.search;
            if (nextSearch !== lastSearchRef.current) {
                queryClient.invalidateQueries({ queryKey: [...TN_LOCATION_QUERY_KEY] });
                lastSearchRef.current = nextSearch;
            }
        };

        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isBrowser, queryClient, enabled]);

    useWatchBrowserGeolocation(queryClient, isBrowser && enabled);

    const fetchLocationFromGraphQL = useCallback(async (): Promise<PositionDetails | null> => {
        try {
            const response = await refetchLocationResolver();
            const location = response.data?.locationResolver;
            if (!location) {
                return null;
            }
            saveLocationToCookie(COOKIE_NAME, location, LOCATION_COOKIE_MAX_AGE);
            return location;
        } catch (err) {
            appLogger.warn(
                'fetchLocationFromGraphQL failed — falling back to default coordinates',
                {
                    error: err instanceof Error ? err.message : String(err),
                },
            );
            return null;
        }
    }, [refetchLocationResolver]);

    const {
        data: location,
        error: queryError,
        isError,
        refetch: queryRefetch,
    } = useQuery({
        queryKey: [...TN_LOCATION_QUERY_KEY],
        queryFn: () => fetchLocationOnClient({ fallback, fetchLocationFromGraphQL }),
        enabled: isBrowser && enabled,
        staleTime: TN_LOCATION_STALE_TIME_MS,
        gcTime: isBrowser ? TN_LOCATION_GC_TIME_MS : undefined,
        retry: 1,
    });

    useEffect(() => {
        if (!enabled) {
            // The hook stays mounted across disable/enable cycles (DMAProvider keeps
            // it alive while inactive); re-arm the one-shot fallback retry so a
            // re-enabled consumer isn't stuck on the IP-fallback market.
            fallbackRefetchAttempted.current = false;
        }
    }, [enabled]);

    useEffect(() => {
        if (
            enabled &&
            location?.isFallback &&
            !fallbackRefetchAttempted.current &&
            isBrowser &&
            typeof navigator !== 'undefined' &&
            'geolocation' in navigator
        ) {
            fallbackRefetchAttempted.current = true;
            queryClient.invalidateQueries({ queryKey: [...TN_LOCATION_QUERY_KEY] });
        }
    }, [location?.isFallback, isBrowser, queryClient, enabled]);

    const refetch = useCallback(async (): Promise<PositionDetails | null> => {
        if (!isBrowser) {
            return null;
        }
        deleteCookie(COOKIE_NAME);
        const result = await queryRefetch();
        return result.data ?? null;
    }, [isBrowser, queryRefetch]);

    const errorMessage = useMemo(
        () => formatLocationQueryError(isError, queryError),
        [isError, queryError],
    );

    return {
        location: location ?? null,
        error: errorMessage,
        refetch,
    };
}
